import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  PLATFORM_ID,
  inject,
  ViewChild,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Event, EventsService, Subevento, TipoTicketConFuncion } from '../../core/services/events.service';
import { MetaService } from '../../core/services/meta.service';
import { WebAppConfigService } from '../../core/services/webapp-config.service';
import { Header } from '../../components/header/header';
import { Footer } from '../../components/footer/footer';
import { ScrollToTop } from '../../components/scroll-to-top/scroll-to-top';

interface HoraGroup {
  hora: string;
  tiposTicket: TipoTicketConFuncion[];
}

@Component({
  selector: 'app-reservar-actividad',
  standalone: true,
  imports: [CommonModule, Header, Footer, ScrollToTop],
  templateUrl: './reservar-actividad.html',
  styleUrl: './reservar-actividad.css',
})
export class ReservarActividadPage implements OnInit, OnDestroy {
  @ViewChild(Header) header?: Header;

  event?: Event;
  subevento?: Subevento;
  fecha = '';
  isLoading = true;
  error = false;

  // Tipotickets agrupados por hora de función
  horaGroups: HoraGroup[] = [];
  isLoadingTickets = false;

  // Horario seleccionado (null = mostrar lista de horarios)
  selectedHoraGroup: HoraGroup | null = null;
  private pendingHora = '';

  private platformId = inject(PLATFORM_ID);
  private isBrowser: boolean;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private eventsService: EventsService,
    private metaService: MetaService,
    private webAppConfig: WebAppConfigService,
    private cdr: ChangeDetectorRef
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    const config = this.webAppConfig.config();
    if (config) {
      this.metaService.resetToDefault(
        this.webAppConfig.getSiteName() + ' - ' + config.short_description,
        config.description,
        config.url_logo,
        config.url
      );
    }

    this.route.params.subscribe(params => {
      const urlSlug = params['url_slug'];
      const slugActividad = params['slug_actividad'];
      this.route.queryParams.subscribe(qp => {
        this.fecha = qp['fecha'] || '';
        this.pendingHora = qp['hora'] || '';
        if (urlSlug && slugActividad && this.fecha) {
          this.loadData(urlSlug, slugActividad, this.fecha);
        } else if (urlSlug && slugActividad) {
          this.router.navigate(['/evento', urlSlug, 'actividad', slugActividad]);
        }
      });
    });
  }

  ngOnDestroy(): void {
    const config = this.webAppConfig.config();
    if (config) {
      this.metaService.resetToDefault(
        this.webAppConfig.getSiteName() + ' - ' + config.short_description,
        config.description,
        config.url_logo,
        config.url
      );
    }
  }

  private loadData(urlSlug: string, slugActividad: string, fecha: string): void {
    this.isLoading = true;
    this.error = false;

    this.eventsService.getEventBySlug(urlSlug).subscribe({
      next: eventRes => {
        if (!eventRes.success || !eventRes.data) {
          this.error = true;
          this.isLoading = false;
          this.cdr.markForCheck();
          return;
        }
        this.event = eventRes.data;

        this.eventsService.getSubeventoBySlug(this.event.eventoid, slugActividad).subscribe({
          next: subRes => {
            if (!subRes.success || !subRes.data) {
              this.error = true;
              this.isLoading = false;
              this.cdr.markForCheck();
              return;
            }
            this.subevento = subRes.data;
            this.isLoading = false;
            this.cdr.markForCheck();

            this.loadTipoTickets(this.event!.eventoid, this.subevento!.subeventoid, fecha);
          },
          error: () => {
            this.error = true;
            this.isLoading = false;
            this.cdr.markForCheck();
          }
        });
      },
      error: () => {
        this.error = true;
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  private loadTipoTickets(eventoid: string, subeventoid: string, fecha: string): void {
    this.isLoadingTickets = true;
    this.horaGroups = [];

    this.eventsService.getTipoTicketsByFecha(eventoid, subeventoid, fecha).subscribe({
      next: res => {
        console.log('ReservarActividad: respuesta de horarios del endpoint', {
          eventoid,
          subeventoid,
          fecha,
          response: res
        });
        if (res.success && res.data) {
          const tiposTicketActivos = res.data.filter(
            (item) => (item.estado || '').toLowerCase() === 'activo'
          );
          this.horaGroups = this.groupByHora(tiposTicketActivos);
          console.log('ReservarActividad: horarios agrupados cargados', this.horaGroups);
          if (this.pendingHora) {
            const match = this.horaGroups.find(g => g.hora === this.pendingHora);
            if (match) this.selectedHoraGroup = match;
            this.pendingHora = '';
          }
        }
        this.isLoadingTickets = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.isLoadingTickets = false;
        this.cdr.markForCheck();
      }
    });
  }

  private groupByHora(items: TipoTicketConFuncion[]): HoraGroup[] {
    const map = new Map<string, TipoTicketConFuncion[]>();
    for (const item of items) {
      const hora = item.funcion?.hora || 'Sin horario';
      if (!map.has(hora)) map.set(hora, []);
      map.get(hora)!.push(item);
    }
    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([hora, tiposTicket]) => ({
        hora,
        tiposTicket: tiposTicket.sort((a, b) =>
          a.nombre_localidad.localeCompare(b.nombre_localidad, 'es', { numeric: true })
        )
      }));
  }

  selectHoraGroup(group: HoraGroup): void {
    this.selectedHoraGroup = group;
  }

  deselectHoraGroup(): void {
    this.selectedHoraGroup = null;
  }

  selectTipoTicket(tt: TipoTicketConFuncion): void {
    if (this.getDisponibilidadEstado(tt) === 'agotado') return;
    if (this.event && this.subevento) {
      this.router.navigate(
        ['/evento', this.event.url_slug, 'actividad', this.subevento.slug, 'comprar', tt.tipoticketid],
        { queryParams: { fecha: this.fecha, hora: this.selectedHoraGroup?.hora || '' } }
      );
    }
  }

  onTicketsAdded(): void {
    if (this.header) {
      this.header.loadCartCount();
    }
  }

  goBackToActividad(): void {
    if (this.event && this.subevento) {
      this.router.navigate(['/evento', this.event.url_slug, 'actividad', this.subevento.slug]);
    }
  }

  goBackToEvent(): void {
    if (this.event) {
      this.router.navigate(['/evento', this.event.url_slug]);
    } else {
      this.router.navigate(['/']);
    }
  }

  getBannerImage(): string {
    // Usar exclusivamente la imagen horizontal del subevento
    if (this.subevento?.imagenes?.horizontal) return this.subevento.imagenes.horizontal;
    return 'https://dummyimage.com/2000x576/cccccc/666666?text=Reservar';
  }

  getActividadImage(): string {
    // Imagen de actividad para mostrar en las cards de coches
    if (this.subevento?.imagenes?.actividad) return this.subevento.imagenes.actividad;
    return '/assets/trenbase.svg';
  }

  formatFecha(fecha: string): string {
    if (!fecha) return '';
    const [yyyy, mm, dd] = fecha.split('-');
    const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    return d.toLocaleDateString('es-ES', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  }

  getDisponiblesReales(tt: TipoTicketConFuncion): number {
    const tickets = Array.isArray(tt.tickets) ? tt.tickets : [];

    // Cuando el endpoint incluye detalle de tickets, usar el estado real del ticket
    // para excluir bloqueados/reservados y evitar sobreventa visual.
    if (tickets.length > 0) {
      return tickets.filter((ticket: any) => (ticket?.estado || '').toLowerCase() === 'disponible').length;
    }

    const estadoCounts = (tt as any).estado_counts;
    if (estadoCounts) {
      return Number(estadoCounts.disponible || 0);
    }

    const reservados = Number(tt.tickets_reservados || 0);
    const bloqueados = Number((tt as any).tickets_bloqueados || 0);
    return Math.max(0, Number(tt.aforo_disponible || 0) - reservados - bloqueados);
  }

  getDisponibilidadPorcentaje(tt: TipoTicketConFuncion): number {
    if (tt.aforo_maximo === 0) return 0;
    return (this.getDisponiblesReales(tt) / tt.aforo_maximo) * 100;
  }

  getDisponibilidadEstado(tt: TipoTicketConFuncion): 'disponible' | 'limitado' | 'agotado' {
    const d = this.getDisponiblesReales(tt);
    if (d === 0) return 'agotado';
    if (d < 10) return 'limitado';
    return 'disponible';
  }

  getPrecioDesde(tt: TipoTicketConFuncion): number | null {
    if (!tt.precios || tt.precios.length === 0) return null;
    return Math.min(...tt.precios.map((p: any) => p.precio_total ?? p.precio_base ?? 0));
  }

  getMoneda(tt: TipoTicketConFuncion): string {
    return tt.precios?.[0]?.nombre_moneda || 'USD';
  }
}
